// ─── Locale-First URL Routing — SWIM ──────────────────────────────────────────
// Pattern: KodeArk/02-Patterns/Locale-First URL Routing.md
// All routes live under /:locale/:lang/*
// Year 1: US-only launch, but locale prefix baked in from day 1.
// Adding new markets = add constants here only. Zero route changes.

export type Locale = 'us'
export type Language = 'en'

export const SUPPORTED_LOCALES: readonly Locale[] = ['us']
export const SUPPORTED_LANGUAGES: readonly Language[] = ['en']
export const DEFAULT_LOCALE: Locale = 'us'
export const DEFAULT_LANGUAGE: Record<Locale, Language> = { us: 'en' }

export function isValidLocale(v: unknown): v is Locale {
  return SUPPORTED_LOCALES.includes(v as Locale)
}

export function isValidLanguage(v: unknown): v is Language {
  return SUPPORTED_LANGUAGES.includes(v as Language)
}

export function getDefaultLanguageForLocale(locale: Locale): Language {
  return DEFAULT_LANGUAGE[locale]
}

/** Build a full locale-prefixed path: /us/en/streams/... */
export function buildLocalePath(locale: Locale, lang: Language, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `/${locale}/${lang}${normalized === '/' ? '' : normalized}`
}

/** Parse /:locale/:lang/rest from a full pathname. Returns null if not a locale path. */
export function parseLocalePath(pathname: string): { locale: Locale; lang: Language; rest: string } | null {
  const match = pathname.match(/^\/([a-z]{2})\/([a-z]{2})(\/.*)?$/)
  if (!match) return null
  const [, locale, lang, rest = '/'] = match
  if (!isValidLocale(locale) || !isValidLanguage(lang)) return null
  return { locale, lang, rest }
}

/**
 * Validate that a redirect target is a safe, same-origin relative path.
 * Rejects protocol-relative URLs (//evil.com, ///evil.com, /\evil.com) and
 * absolute URLs (https://evil.com) — browsers can interpret these as
 * off-site navigation even though they look like a local path. Use on any
 * ?from=/?redirect= query param before passing it to navigate() or building
 * an external callback URL (e.g. OAuth) from it.
 */
export function sanitizeRedirectTarget(target: string, fallback: string = '/'): string {
  const isProtocolRelative = target.startsWith('//') || target.startsWith('/\\')
  const isSafeRelativePath = target.startsWith('/') && !isProtocolRelative
  return isSafeRelativePath ? target : fallback
}
